import { Combinations } from '@givebacks/utils/types';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { useAuthentication } from '../Idp/useAuthentication';
import { QueryPagination, createQueryOptions } from '../query';
import { ExportQueryFn } from '../query/hooks';
import { RequestMeta } from '../temporary/requestmeta.types';
import { useBackerApi } from './api';
import { ListsResponse } from './lists/lists.types';
import { ListsSearch } from './lists/search.types';
import { backerKeys } from './query-key-factory';
import {
  Backer,
  BackerCauseResponse,
  BackerSearch,
  CauseBackersResponse,
  ConsumerCauseBackerResponse,
  GetBackersMeta,
  ShowBackerInfoForCauseResponse,
} from './types';

type BackerResponse = {
  backer: Backer;
};

// get backer information
const useShowBackerInfo = () => {
  const { isAuthenticated } = useAuthentication();
  const BackerApi = useBackerApi();
  const getBackerCause = async () => {
    const { data } = await BackerApi.get<BackerResponse>('/backers/me');
    return data.backer;
  };
  return useQuery({
    queryKey: backerKeys.me,
    queryFn: getBackerCause,
    enabled: isAuthenticated,
  });
};

// get backer cause
const useShowCauseBackerInfo = (causeId?: string) => {
  const BackerApi = useBackerApi();
  const getBackerCause = async () => {
    const { data } = await BackerApi.get<BackerCauseResponse>(`causes/${causeId}`);
    return data.cause;
  };

  const key = causeId ? backerKeys.causes.id(causeId).all : [];

  return useQuery({
    queryKey: key,
    queryFn: getBackerCause,
    enabled: !!causeId,
  });
};

const useShowBackerInfoForCause = ({ causeId }: { causeId: string }) => {
  const BackerApi = useBackerApi();

  const getBackerInfoForCause = async () => {
    const { data } = await BackerApi.get<ShowBackerInfoForCauseResponse>(
      `causes/${causeId}/backer`
    );

    return data.backer;
  };

  const key = causeId ? backerKeys.causes.id(causeId).backer : [];

  return useQuery({
    queryKey: key,
    queryFn: getBackerInfoForCause,
    enabled: !!causeId,
  });
};

// show all backers
// meta pagination
// Search by: name (first, last), email, activity
// Order by: name, created_at, purchases, amount_raised, extensions (mobile, desktop? what about both?), cards on file, referrals,
// address, state, zipcode? ... should this be just state or zipcode?

// how should I get cause_id if not passing it in?

const useGetCauseBackers = (cause_id?: string) => {
  const BackerApi = useBackerApi();
  return async (context: { meta?: GetBackersMeta }): Promise<CauseBackersResponse> => {
    const { queryString } = context.meta || {};
    const { data } = await BackerApi.get<CauseBackersResponse>(
      `causes/${cause_id}/backers?${queryString}`
    );
    return data;
  };
};

const useListCauseBackers = ({
  cause_id: causeId,
  queryOptions,
  pagination,
}: {
  cause_id?: string;
  queryOptions?: BackerSearch;
  pagination?: QueryPagination;
}) => {
  const getCauseBackers = useGetCauseBackers(causeId);
  const { queryString, queryObject, queryPagination } = createQueryOptions<BackerSearch>({
    search: queryOptions,
    pagination,
  });

  const key = causeId ? backerKeys.causes.id(causeId).backers.all : [];

  return useQuery({
    queryKey: [...key, queryObject, queryPagination],
    queryFn: getCauseBackers,
    enabled: !!causeId,
    meta: { queryString, causeId },
  });
};

//export backer info
const useExportBackerInfo: ExportQueryFn<{ data: Backer[] }> = ({
  isExporting,
  queryOptions,
  causeId,
}: {
  isExporting: boolean;
  queryOptions?: BackerSearch;
  causeId?: string;
}) => {
  const getCauseBackers = useGetCauseBackers(causeId);

  const exportData = async ({ pageParam }: { pageParam: { limit: number; offset: number } }) => {
    const { queryString } = createQueryOptions<BackerSearch>({
      search: { ...queryOptions },
      pagination: {
        limit: pageParam.limit,
        offset: pageParam.offset,
      },
    });
    const result = await getCauseBackers({ meta: { queryString, causeId: causeId } });
    return { data: result.backers, ...result };
  };

  const key = causeId ? backerKeys.causes.id(causeId).backersExport : [];

  return useInfiniteQuery({
    queryKey: key,
    queryFn: exportData,
    enabled: !!causeId && !!isExporting,
    initialPageParam: { limit: 250, offset: 0 },
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      const { meta } = lastPage as { meta: RequestMeta };
      if (!meta) return undefined;
      const { has_more, limit, offset } = meta;
      if (!has_more) {
        return undefined;
      }
      return { limit, offset: offset + limit };
    },
  });
};

/**
 * Fetch consumer-facing cause details with optional include fields.
 *
 * Available include params:
 * - `student_fees_access`: adds a boolean `student_fees_access` field to the response.
 *   This value is true if the cause either:
 *   - has students on its roster, or
 *   - has at least one student fee in the `published` state.
 *
 * @param param0.causeId The cause ID to fetch.
 * @param param0.include Include options (currently supports `student_fees_access`).
 * @returns React Query result resolving to the cause, optionally augmented by the requested include fields.
 */
const useShowBackerConsumerCause = <T = ConsumerCauseBackerResponse['cause']>({
  causeId,
  include,
  select,
}: {
  causeId?: string;
  /**
   * Include options (currently supports `student_fees_access`).
   */
  include?: Combinations<'student_fees_access'>;
  select?: (data: ConsumerCauseBackerResponse['cause']) => T;
}) => {
  const BackerApi = useBackerApi();

  const getBackerInfoForCause = async () => {
    const { data } = await BackerApi.get<ConsumerCauseBackerResponse>(
      `/consumer/causes/${causeId}`,
      {
        params: {
          include,
        },
      }
    );
    return data.cause;
  };

  const key = causeId ? backerKeys.consumer.causes.id(causeId).all : [];
  return useQuery({
    queryKey: [...key, { include }],
    queryFn: getBackerInfoForCause,
    enabled: !!causeId,
    select,
  });
};

const useShowBackerByCause = ({
  causeId,
  backerId,
  include,
}: {
  causeId?: string;
  backerId?: string;
  include?: string;
}) => {
  const BackerApi = useBackerApi();

  const get = async () => {
    const { data } = await BackerApi.get<{ backer: Backer }>(
      `/causes/${causeId}/backers/${backerId}?include=${include}`
    );
    return data.backer;
  };

  const key = causeId && backerId ? backerKeys.causes.id(causeId).backers.id(backerId).all : [];
  return useQuery({
    queryKey: key,
    queryFn: get,
    enabled: !!causeId && !!backerId,
  });
};

const useShowBackerLists = ({
  causeId,
  backerId,
  queryOptions,
}: {
  causeId?: string;
  backerId?: string;
  queryOptions?: ListsSearch;
}) => {
  const BackerApi = useBackerApi();

  const getBackerLists = async () => {
    const { queryString } = createQueryOptions<ListsSearch>({
      search: queryOptions,
    });

    const { data } = await BackerApi.get<ListsResponse>(
      `/causes/${causeId}/backers/${backerId}/lists?${queryString}`
    );
    return data.lists;
  };

  const key = causeId && backerId ? backerKeys.causes.id(causeId).backers.id(backerId).lists : [];

  return useQuery({
    queryKey: [...key, queryOptions],
    queryFn: getBackerLists,
    enabled: !!causeId && !!backerId,
  });
};

export {
  useExportBackerInfo,
  useListCauseBackers,
  useShowBackerByCause,
  useShowBackerConsumerCause,
  useShowBackerInfo,
  useShowBackerInfoForCause,
  useShowBackerLists,
  useShowCauseBackerInfo,
};
